/**
 * Единая функция «что показать про этот байк» — тон, ярлык, причины.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ПОЧЕМУ ОДНА ФУНКЦИЯ, А НЕ ТРИ (pickup.ts + vehicle-filters.ts + ярлыки
 * статусов, как было раньше)
 * ─────────────────────────────────────────────────────────────────────────
 * Три места отвечали на один вопрос «почему этот байк особенный» и
 * расходились бы на первой же правке порога. Теперь источник один:
 * карточка байка, список «Забрать» и цвет метки на карте читают отсюда.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ ЭТО ЛИСТ. Импортов из `api/*`, `lib/vehicle-status.ts`,
 * `lib/offline/outbox.ts` здесь быть не должно — прямо или транзитивно.
 * `lib/vehicle-status.ts` сам импортирует `lib/offline/outbox.ts`
 * (`statusActionPath`), и обратный импорт отсюда замкнул бы ровно тот цикл,
 * из-за которого «Вернуть в линию» однажды улетело на несуществующий
 * `/available` вместо `/recovered`. Единственный runtime-импорт ниже —
 * `./zones`, который сам ничего не тянет, кроме wkt/turf.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * СЕРВЕР (C1: problem/problemDetail/faultReason) — ДОБАВКА, А НЕ ЗАМЕНА
 * ─────────────────────────────────────────────────────────────────────────
 * Спецификация просит «если бэк отдаёт problem — используем его первым».
 * Буквально так сделать нельзя: `problem` — обычная строка с `.default('')`,
 * а не `optional bool`, как `runtime_stale`. Пустая строка от старого
 * бэкенда, который поля не знает, и пустая строка от нового бэкенда,
 * подтверждающего «проблем нет», на проводе неразличимы (та же ловушка,
 * которую optional bool у runtime_stale решает, а plain string — нет).
 *
 * Поэтому эвристика по сырым сигналам (batteryPct/runtimeStale/lastSeenAt/
 * зоны) считается ВСЕГДА — она не зависит от выката C1 и не может дать
 * ложное «всё в порядке». Серверные поля используются, чтобы:
 *   а) заменить текст СОВПАВШЕЙ причины более точным (сервер знает
 *      расстояние до границы зоны в метрах — клиент только факт «снаружи»);
 *   б) добавить причину, которую клиент не мог увидеть сам (зоны ещё не
 *      загрузились, но сервер точно знает).
 * `problem === ''` никогда не подавляет то, что уже нашла эвристика.
 */
import { rideZoneVerdict, type RideZone } from './zones'
import { heldByCollectorLabel } from './vehicle-status'

export type BikeTone = 'ok' | 'blue' | 'red' | 'orange' | 'purple' | 'grey' | 'neutral'

/**
 * `unknown_battery` — снимок протух, заряд неизвестен. Отдельный код, а не
 * `low_battery`: это догадка, и в заголовок она выходит последней — после
 * фактов «не на связи» и «за зоной». В «Разряженные» он попадает всё равно
 * (`lib/work-filter.ts`).
 */
export type BikeReasonCode = 'low_battery' | 'unknown_battery' | 'out_of_zone' | 'faulty' | 'offline'

/** Коды, которые сервер (C1 `problem`) может прислать сам. */
type ServerCode = Exclude<BikeReasonCode, 'faulty' | 'unknown_battery'>

export type BikeReason = { code: BikeReasonCode; text: string }

export type BikeStatus = {
  tone: BikeTone
  /** Короткий ярлык для шапки карточки и попапа на карте. */
  label: string
  /** Причины, отсортированные по срочности. Пусто — причин нет. */
  reasons: BikeReason[]
  /** true — байку место в списке «Забрать». */
  needsPickup: boolean
}

/** Тот же порог, что был в api/vehicles.ts. Владеет им теперь этот файл. */
export const LOW_BATTERY_PCT = 20

/** Контракт C1: телеметрия старше получаса — офлайн. */
const OFFLINE_AFTER_MIN = 30

export type DescribableBike = {
  /** available | reserved | in_ride | faulty | lost | in_transit | at_base | servicing | written_off | in_pool */
  status: string
  batteryPct: number
  /** Три состояния с провода: отсутствует/undefined — сервер не знает, true — снимок протух. */
  runtimeStale?: boolean | undefined
  /** RFC3339 UTC. Пустая строка = никогда не выходил на связь. */
  lastSeenAt: string
  lat: number
  lng: number
  /** C1, необязательные: сервер их ещё может не отдавать. */
  problem?: string
  problemDetail?: string
  faultReason?: string
  /** Кто держит in_pool/in_transit: "" | driver | collector (волна 3). */
  poolKind?: string
}

function hasPosition(b: Pick<DescribableBike, 'lat' | 'lng'>): boolean {
  return b.lat !== 0 || b.lng !== 0
}

function minutesSince(iso: string, now: Date): number {
  if (!iso) return Number.POSITIVE_INFINITY
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return Number.POSITIVE_INFINITY
  return (now.getTime() - then) / 60_000
}

function offlineText(minutes: number): string {
  if (!Number.isFinite(minutes)) return 'Не на связи'
  const h = minutes / 60
  if (h < 1) return `Не на связи ${Math.max(1, Math.round(minutes))} мин`
  return `Не на связи ${Math.round(h)} ч`
}

/** Статусы, для которых тон/ярлык решает сам статус — эвристика не нужна. */
function describeByStatus(bike: DescribableBike): BikeStatus | null {
  switch (bike.status) {
    case 'in_ride':
      return { tone: 'blue', label: 'В аренде', reasons: [], needsPickup: false }
    case 'reserved':
      return { tone: 'blue', label: 'Забронирован', reasons: [], needsPickup: false }
    case 'faulty':
    case 'lost': {
      // ⚠ Контракт C1 объединяет faulty и lost в один problem=faulty: обе
      // причины «байк не может ездить» и обе — повод забрать. Ярлык при
      // этом остаётся своим для каждого статуса, чтобы не путать «сломан»
      // с «потерян».
      const label =
        bike.status === 'lost'
          ? 'Утерян'
          : bike.faultReason
            ? `Сломан: ${bike.faultReason}`
            : 'Сломан'
      return {
        tone: 'red',
        label,
        reasons: [{ code: 'faulty', text: bike.faultReason || 'сломан' }],
        needsPickup: true,
      }
    }
    case 'in_transit':
      return {
        tone: 'neutral',
        label: heldByCollectorLabel(bike.status, bike.poolKind ?? '') ?? 'В машине',
        reasons: [],
        needsPickup: false,
      }
    case 'at_base':
      return { tone: 'neutral', label: 'На базе', reasons: [], needsPickup: false }
    case 'servicing':
      return { tone: 'neutral', label: 'В ремонте', reasons: [], needsPickup: false }
    case 'written_off':
      return { tone: 'neutral', label: 'Списан', reasons: [], needsPickup: false }
    // Водитель уже едет за ним — работа занята, «Забрать» второму не нужно.
    case 'in_pool':
      return {
        tone: 'neutral',
        label: heldByCollectorLabel(bike.status, bike.poolKind ?? '') ?? 'В пуле',
        reasons: [],
        needsPickup: false,
      }
    default:
      return null
  }
}

const REASON_LABEL: Record<ServerCode, string> = {
  low_battery: 'Разряжен',
  out_of_zone: 'За зоной',
  offline: 'Не на связи',
}

/** Причины по сырым сигналам — работает независимо от того, выкачен ли C1. */
function heuristicReasons(
  bike: DescribableBike,
  zones: readonly RideZone[] | undefined,
  now: Date,
): BikeReason[] {
  const out: BikeReason[] = []

  if (bike.runtimeStale === false && bike.batteryPct <= LOW_BATTERY_PCT) {
    out.push({ code: 'low_battery', text: `Разряжен ${bike.batteryPct} %` })
  }
  /*
   * Снимок протух — заряд неизвестен, и процента в причине нет: ноль такого
   * снимка не показание. Но молчащий байк чаще всего сел, и из объезда на
   * зарядку он выпадать не должен — так же решает админка (`isLowBattery`).
   * `undefined` (сервер про телеметрию не говорит) сюда НЕ входит: иначе
   * «Разряженные» собрали бы весь парк.
   */
  if (bike.runtimeStale === true) {
    out.push({ code: 'unknown_battery', text: 'Заряд неизвестен' })
  }

  // «За зоной» проверяется только у байков с координатами: (0,0) — это
  // «неизвестно», а не точка в Атлантике. Список зон ещё не загрузился —
  // тоже не нарушение, а незнание (rideZoneVerdict возвращает 'unknown').
  if (hasPosition(bike) && zones !== undefined && zones.length > 0) {
    const verdict = rideZoneVerdict(bike, zones)
    if (verdict.out === true) {
      out.push({ code: 'out_of_zone', text: 'За зоной катания' })
    }
  }

  const minutes = minutesSince(bike.lastSeenAt, now)
  if (minutes >= OFFLINE_AFTER_MIN) {
    out.push({ code: 'offline', text: offlineText(minutes) })
  }

  return out
}

/** Серверные codes из C1, которые имеет смысл сливать со списком причин. */
const MERGEABLE_CODES = new Set<string>(['low_battery', 'out_of_zone', 'offline'])

/**
 * Сливает эвристику с серверным `problem`/`problemDetail`.
 *
 * Сервер уточняет текст совпавшей причины (знает метры до границы зоны —
 * клиент знает только факт) и может добавить причину, которую клиент не
 * увидел сам (зоны ещё не загрузились). `problem`, не входящий в этот
 * набор (`''`, `in_ride`, `faulty` — последний уже обработан статусом),
 * на список причин не влияет.
 */
function mergeServerProblem(reasons: BikeReason[], bike: DescribableBike): BikeReason[] {
  const problem = bike.problem
  if (!problem || !MERGEABLE_CODES.has(problem)) return reasons
  const code = problem as BikeReasonCode
  const text = bike.problemDetail || REASON_LABEL[code as ServerCode]

  const existing = reasons.find((r) => r.code === code)
  if (existing) {
    return reasons.map((r) => (r.code === code ? { code, text } : r))
  }
  return [...reasons, { code, text }]
}

const URGENCY: BikeReasonCode[] = [
  'faulty',
  'low_battery',
  'out_of_zone',
  'offline',
  'unknown_battery',
]

function byUrgency(reasons: BikeReason[]): BikeReason[] {
  return [...reasons].sort((a, b) => URGENCY.indexOf(a.code) - URGENCY.indexOf(b.code))
}

const TONE_FOR: Record<BikeReasonCode, BikeTone> = {
  low_battery: 'orange',
  unknown_battery: 'orange',
  out_of_zone: 'purple',
  faulty: 'red',
  offline: 'grey',
}

/**
 * Что показать про байк: тон бейджа/метки, ярлык, отсортированные причины.
 *
 * `now` — только для тестов (фиксированное «сейчас» вместо `new Date()`).
 */
export function describeBike(
  bike: DescribableBike,
  zones: readonly RideZone[] | undefined,
  now: Date = new Date(),
): BikeStatus {
  const byStatus = describeByStatus(bike)
  if (byStatus) return byStatus

  const reasons = byUrgency(mergeServerProblem(heuristicReasons(bike, zones, now), bike))

  if (reasons.length === 0) {
    return { tone: 'ok', label: 'В линии', reasons: [], needsPickup: false }
  }

  const top = reasons[0]!
  return {
    tone: TONE_FOR[top.code],
    label: top.text,
    reasons,
    needsPickup: true,
  }
}

type Telemetry = { runtimeStale?: boolean | undefined }

/**
 * Заряд словами — один на шапку, всплывашку, список и пул.
 *
 * Протухший снимок — «нет связи», а не «0 %»: ноль там — нулевое значение
 * поля, а не показание, и водитель поехал бы заряжать молчащий байк.
 * Последнего известного процента у приложения нет — живой опрос
 * («Обновить GPS») делает снимок свежим сам (`applyDeviceReading`).
 */
export function batteryText(bike: Telemetry & { batteryPct: number }): string {
  if (bike.runtimeStale === false) return `${bike.batteryPct} %`
  return bike.runtimeStale ? 'нет связи' : 'заряд —'
}

/** Замок словами. Без свежего снимка — никогда не «открыт»: это ноль, а не дужка. */
export function lockText(bike: Telemetry & { locked: boolean }): string {
  if (bike.runtimeStale === false) return bike.locked ? 'замок закрыт' : 'замок открыт'
  return bike.runtimeStale ? 'замок — нет связи' : 'замок —'
}

/** ≤512 символов — предел `note` в fleet. Режем с запасом, а не надеемся. */
const NOTE_MAX = 512

/**
 * Заметка диспетчеру при заведении задачи «Забрать».
 *
 * Пустой список причин — не ошибка: скаут вправе забрать байк, который ни
 * под один признак не подходит (главный полевой случай — «нашёл байк, на
 * который задачи нет»).
 */
export function pickupNoteFor(status: BikeStatus): string {
  const text =
    status.reasons.length === 0
      ? 'Отмечено скаутом в поле'
      : status.reasons.map((r) => r.text).join(' · ')
  return text.length > NOTE_MAX ? text.slice(0, NOTE_MAX) : text
}
